// =============================================================================
// BulkNsChange — tryb „Bulk" panelu Zmiana DNS: zaznacz domeny → pobierz obecne
// NS wszystkich → ustaw WSPÓLNE NS dla zaznaczonych, a wybranym domenom OSOBNE.
// Plan (co się zmieni / co pominięte i dlaczego) → potwierdzenie z listą
// stare → nowe → wykonanie sekwencyjne z auto-stopem po 3 błędach z rzędu.
// Logika: services/bulkNsChange.ts (czysta, z testami). Log: dns_change_log.
//
// SPONSORAKI: konto przychodzi z rodzica jako `workspace`, ale komponent sam
// jeszcze raz wymusza „firma" bez flagi can_access_sideprojects (twardy guard
// poza UI). Lista domen pochodzi z `websites` (RLS can_see_workspace).
// =============================================================================

import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Download, Loader2, Save, Plus, AlertTriangle, ArrowRight, CheckCircle2, XCircle, MinusCircle, Square, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useCanAccessSideprojects } from "@/hooks/useCanAccessSideprojects";
import { logPublic, logError } from "@/services/contentEngine/contentLogger";
import { nsGet, nsSet, normalizeDomain, WORKSPACE_LABEL, type AmWorkspace } from "@/services/aftermarketService";
import { insertDnsChangeLog } from "@/services/cloudflare/dnsChangeLogService";
import {
  buildBulkNsPlan, parseNsInput, runBulkNs, validateNs,
  type BulkNsRowInput, type BulkNsRowState, type BulkNsSummary,
} from "@/services/bulkNsChange";

interface BulkNsDomain {
  key: string;
  websiteId: string | null;
  expiry: string | null;
  /** expiry_source === "not_on_account" → domeny nie ma na koncie AfterMarket */
  source: string | null;
}

interface Props {
  workspace: AmWorkspace;
  domains: BulkNsDomain[];
  loading?: boolean;
}

interface FetchState { ns: string[] | null; error?: string; fetching?: boolean }

const FETCH_CONCURRENCY = 3; // tylko odczyt (ns/get); zapis jest zawsze po jednej domenie

export function BulkNsChange({ workspace, domains, loading = false }: Props) {
  const { toast } = useToast();
  // Twardy guard poza UI: bez flagi zawsze konto firmowe, niezależnie od propa.
  const { canAccess: canSideprojects } = useCanAccessSideprojects();
  const ws: AmWorkspace = canSideprojects === true ? workspace : "firma";

  const [extra, setExtra] = useState<BulkNsDomain[]>([]);
  const [manual, setManual] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [fetched, setFetched] = useState<Record<string, FetchState>>({});
  const [sharedText, setSharedText] = useState("");
  const [custom, setCustom] = useState<Record<string, { on: boolean; text: string }>>({});
  const [fetching, setFetching] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [runStates, setRunStates] = useState<Record<string, BulkNsRowState>>({});
  const [summary, setSummary] = useState<BulkNsSummary | null>(null);
  const stopRef = useRef(false);

  const all = useMemo(() => {
    const seen = new Set<string>();
    return [...domains, ...extra].filter((d) => (seen.has(d.key) ? false : (seen.add(d.key), true)));
  }, [domains, extra]);
  const shown = useMemo(() => (q.trim() ? all.filter((d) => d.key.includes(q.trim().toLowerCase())) : all), [all, q]);
  const selectedList = useMemo(() => all.filter((d) => selected.has(d.key)), [all, selected]);

  // Zamknięcie karty w trakcie zapisu = część domen zmieniona, część nie
  useEffect(() => {
    if (!running) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [running]);

  const toggle = (key: string, on: boolean) => setSelected((prev) => { const n = new Set(prev); if (on) n.add(key); else n.delete(key); return n; });
  const allShownSelected = shown.length > 0 && shown.every((d) => selected.has(d.key));
  const toggleShown = (on: boolean) => setSelected((prev) => { const n = new Set(prev); for (const d of shown) { if (on) n.add(d.key); else n.delete(d.key); } return n; });

  const addManual = () => {
    const key = normalizeDomain(manual);
    if (!key || !key.includes(".")) { toast({ title: "Podaj poprawną domenę", variant: "destructive" }); return; }
    if (!all.some((d) => d.key === key)) setExtra((p) => [...p, { key, websiteId: null, expiry: null, source: "manual" }]);
    toggle(key, true);
    setManual("");
  };

  // --- pobranie obecnych NS dla zaznaczonych (odczyt, 3 naraz) -----------------
  const fetchNs = async (onlyMissing: boolean) => {
    const todo = selectedList.filter((d) => !onlyMissing || !fetched[d.key] || fetched[d.key].ns === null);
    if (!todo.length) { toast({ title: "Obecne NS są już pobrane dla zaznaczonych domen" }); return; }
    setFetching(true);
    setSummary(null);
    setRunStates({});
    setFetched((p) => { const n = { ...p }; for (const d of todo) n[d.key] = { ns: null, fetching: true }; return n; });
    const queue = [...todo];
    let failed = 0;
    const worker = async () => {
      for (;;) {
        const d = queue.shift();
        if (!d) return;
        try {
          const ns = await nsGet(d.key, ws);
          setFetched((p) => ({ ...p, [d.key]: { ns } }));
        } catch (e) {
          failed++;
          setFetched((p) => ({ ...p, [d.key]: { ns: null, error: e instanceof Error ? e.message : String(e) } }));
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(FETCH_CONCURRENCY, todo.length) }, worker));
    setFetching(false);
    toast({
      title: `Pobrano NS: ${todo.length - failed}/${todo.length}`,
      description: failed ? `${failed} domen bez odpowiedzi — nie zostaną zmienione, dopóki nie pobierzesz ich NS.` : undefined,
      variant: failed ? "destructive" : undefined,
    });
  };

  // --- plan ---------------------------------------------------------------------
  const sharedNs = useMemo(() => parseNsInput(sharedText), [sharedText]);
  const sharedError = sharedText.trim() ? validateNs(sharedNs) : null;
  const inputs: BulkNsRowInput[] = useMemo(() => selectedList.map((d) => ({
    domain: d.key,
    websiteId: d.websiteId,
    currentNs: fetched[d.key]?.ns ?? null,
    fetchError: fetched[d.key]?.error ?? null,
    custom: Boolean(custom[d.key]?.on),
    customNs: parseNsInput(custom[d.key]?.text || ""),
  })), [selectedList, fetched, custom]);
  const plan = useMemo(() => buildBulkNsPlan(inputs, sharedNs), [inputs, sharedNs]);
  const planByDomain = useMemo(() => new Map(plan.rows.map((r) => [r.domain, r])), [plan]);
  const skipCounts = useMemo(() => {
    const c = { same: 0, no_current: 0, invalid_target: 0 };
    for (const r of plan.rows) if (r.skip) c[r.skip]++;
    return c;
  }, [plan]);
  const fetchedCount = selectedList.filter((d) => fetched[d.key]?.ns != null).length;

  // Zestawy NS, które już występują na zaznaczonych domenach — szybkie wypełnienie pola „wspólne"
  const knownSets = useMemo(() => {
    const m = new Map<string, { ns: string[]; count: number }>();
    for (const d of selectedList) {
      const ns = fetched[d.key]?.ns;
      if (!ns || ns.length < 2) continue;
      const k = parseNsInput(ns).sort().join("|");
      const e = m.get(k);
      if (e) e.count++; else m.set(k, { ns: parseNsInput(ns), count: 1 });
    }
    return [...m.values()].sort((a, b) => b.count - a.count).slice(0, 4);
  }, [selectedList, fetched]);

  const setCustomOn = (key: string, on: boolean) => setCustom((p) => ({
    ...p,
    [key]: { on, text: p[key]?.text ?? (fetched[key]?.ns || []).join(", ") },
  }));

  // --- wykonanie ------------------------------------------------------------------
  const run = async () => {
    setConfirmOpen(false);
    if (!plan.changeCount) return;
    const frozen = plan; // plan z chwili potwierdzenia — edycja pól w trakcie nic nie zmienia
    stopRef.current = false;
    setRunning(true);
    setSummary(null);
    setRunStates(Object.fromEntries(frozen.rows.map((r) => [r.domain, { domain: r.domain, status: "pending" as const }])));
    try {
      const s = await runBulkNs(
        frozen,
        ws,
        { nsSet, log: insertDnsChangeLog },
        (st) => {
          setRunStates((p) => ({ ...p, [st.domain]: st }));
          if (st.status === "ok") {
            const row = frozen.rows.find((r) => r.domain === st.domain);
            if (row) {
              setFetched((p) => ({ ...p, [st.domain]: { ns: row.targetNs } }));
              logPublic("AfterMarket", `[${WORKSPACE_LABEL[ws]}] Bulk NS zmienione: ${st.domain} → ${row.targetNs.join(", ")} (było: ${row.currentNs?.length ? row.currentNs.join(", ") : "brak"})`);
            }
          }
          if (st.status === "failed") logError("AfterMarket", `[${WORKSPACE_LABEL[ws]}] Bulk NS nieudane: ${st.domain} — ${st.message || "błąd"}`);
        },
        { isStopRequested: () => stopRef.current },
      );
      setSummary(s);
      toast({
        title: s.failed || s.stoppedReason ? "Zmiana NS zakończona z uwagami" : "Nameservery zmienione ✓",
        description: `zmienione ${s.ok} · błędy ${s.failed} · pominięte ${s.skipped}${s.stoppedReason === "consecutive_failures" ? " · zatrzymano po 3 błędach z rzędu" : s.stoppedReason === "user" ? " · zatrzymano ręcznie" : ""}. Propagacja DNS może potrwać do 24h.`,
        variant: s.failed || s.stoppedReason === "consecutive_failures" ? "destructive" : undefined,
      });
    } finally {
      setRunning(false);
    }
  };

  const doneCount = Object.values(runStates).filter((s) => s.status === "ok" || s.status === "failed" || s.status === "skipped").length;
  const busy = running || fetching;

  const statusCell = (key: string) => {
    const rs = runStates[key];
    if (rs) {
      if (rs.status === "running") return <span className="flex items-center gap-1 text-xs"><Loader2 className="h-3.5 w-3.5 animate-spin" /> zapisuję…</span>;
      if (rs.status === "ok") return <span className="flex items-center gap-1 text-xs text-emerald-500"><CheckCircle2 className="h-3.5 w-3.5" /> zmienione</span>;
      if (rs.status === "failed") return <span className="flex items-start gap-1 text-xs text-red-500" title={rs.message}><XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> <span className="line-clamp-2">{rs.message || "błąd"}</span></span>;
      if (rs.status === "skipped") return <span className="flex items-start gap-1 text-xs text-muted-foreground" title={rs.message}><MinusCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> <span className="line-clamp-2">{rs.message || "pominięte"}</span></span>;
      return <span className="text-xs text-muted-foreground">czeka</span>;
    }
    if (!selected.has(key)) return null;
    const p = planByDomain.get(key);
    if (!p) return null;
    if (p.action === "change") return <Badge variant="outline" className="border-amber-500/40 text-[10px] text-amber-500">do zmiany</Badge>;
    return <span className="text-xs text-muted-foreground line-clamp-2" title={p.reason}>{p.reason}</span>;
  };

  return (
    <div className="space-y-5">
      {/* 1. Wybór domen + obecne NS */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Label className="text-sm">1. Zaznacz domeny <span className="ml-1 text-xs font-normal text-muted-foreground">{all.length} na koncie {WORKSPACE_LABEL[ws]} · zaznaczone {selectedList.length}</span></Label>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="szukaj domeny" className="ml-auto h-8 w-full max-w-52 text-xs" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input value={manual} onChange={(e) => setManual(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addManual(); }} placeholder="domena spoza listy, np. przyklad.pl" className="h-8 w-full max-w-64 text-xs" disabled={busy} />
          <Button size="sm" variant="outline" className="h-8" onClick={addManual} disabled={busy || !manual.trim()}><Plus className="mr-1 h-3.5 w-3.5" /> Dodaj</Button>
          <Button size="sm" variant="outline" className="ml-auto h-8" onClick={() => void fetchNs(true)} disabled={busy || !selectedList.length}>
            {fetching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
            Pobierz obecne NS ({selectedList.length})
          </Button>
          {fetchedCount > 0 && (
            <Button size="sm" variant="ghost" className="h-8" onClick={() => void fetchNs(false)} disabled={busy} title="Pobierz ponownie dla wszystkich zaznaczonych">
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>

        <div className="max-h-[55vh] overflow-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-muted text-xs text-muted-foreground">
              <tr>
                <th className="w-9 px-2 py-2 text-left"><Checkbox checked={allShownSelected} onCheckedChange={(v) => toggleShown(v === true)} disabled={busy || !shown.length} aria-label="Zaznacz widoczne" /></th>
                <th className="px-2 py-2 text-left font-medium">Domena</th>
                <th className="px-2 py-2 text-left font-medium">Obecne NS</th>
                <th className="px-2 py-2 text-left font-medium">Nowe NS</th>
                <th className="w-44 px-2 py-2 text-left font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading && <tr><td colSpan={5} className="px-3 py-6 text-center text-xs text-muted-foreground"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Ładuję domeny…</td></tr>}
              {!loading && !shown.length && <tr><td colSpan={5} className="px-3 py-6 text-center text-xs text-muted-foreground">Brak domen na tym koncie w panelu. Dodaj domenę ręcznie powyżej.</td></tr>}
              {shown.map((d) => {
                const on = selected.has(d.key);
                const f = fetched[d.key];
                const c = custom[d.key];
                const p = planByDomain.get(d.key);
                return (
                  <tr key={d.key} className={on ? "bg-primary/5" : undefined}>
                    <td className="px-2 py-1.5 align-top"><Checkbox checked={on} onCheckedChange={(v) => toggle(d.key, v === true)} disabled={busy} aria-label={`Zaznacz ${d.key}`} /></td>
                    <td className="px-2 py-1.5 align-top">
                      <div className="font-mono text-xs">{d.key}</div>
                      <div className="flex flex-wrap items-center gap-1 text-[10px] text-muted-foreground">
                        {d.expiry && <span>wygasa {d.expiry}</span>}
                        {d.source === "not_on_account" && <Badge variant="outline" className="border-amber-500/40 px-1 py-0 text-[9px] text-amber-500">brak na AfterMarket</Badge>}
                        {d.source === "manual" && <Badge variant="outline" className="px-1 py-0 text-[9px]">spoza panelu</Badge>}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 align-top font-mono text-[11px]">
                      {f?.fetching ? <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                        : f?.error ? <span className="font-sans text-red-500 line-clamp-2" title={f.error}>{f.error}</span>
                        : f?.ns ? (f.ns.length ? f.ns.map((n) => <div key={n}>{n}</div>) : <span className="font-sans text-muted-foreground">brak ustawionych</span>)
                        : <span className="font-sans text-muted-foreground">{on ? "nie pobrano" : "—"}</span>}
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      {on && (
                        <div className="space-y-1">
                          <label className="flex items-center gap-2 text-[11px] text-muted-foreground">
                            <Switch checked={Boolean(c?.on)} onCheckedChange={(v) => setCustomOn(d.key, v)} disabled={busy} className="scale-75" />
                            {c?.on ? "osobne NS dla tej domeny" : "wspólne"}
                          </label>
                          {c?.on ? (
                            <Input value={c.text} onChange={(e) => setCustom((prev) => ({ ...prev, [d.key]: { on: true, text: e.target.value } }))} placeholder="ns1.przyklad.pl, ns2.przyklad.pl" className="h-7 font-mono text-[11px]" disabled={busy} />
                          ) : (
                            p?.action === "change" && <div className="font-mono text-[11px] text-emerald-500">{p.targetNs.map((n) => <div key={n}>{n}</div>)}</div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1.5 align-top">{statusCell(d.key)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* 2. Wspólne NS */}
      <div className="space-y-2">
        <Label className="text-sm">2. Wspólne nameservery <span className="ml-1 text-xs font-normal text-muted-foreground">dla wszystkich zaznaczonych bez przełącznika „osobne” (min. 2, po jednym w linii albo po przecinku)</span></Label>
        <Textarea value={sharedText} onChange={(e) => setSharedText(e.target.value)} rows={3} placeholder={"chip.ns.cloudflare.com\nkami.ns.cloudflare.com"} className="font-mono text-sm" disabled={busy} />
        {sharedError && <p className="text-xs text-red-500">{sharedError}</p>}
        {knownSets.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            Użyj zestawu z zaznaczonych domen:
            {knownSets.map((s) => (
              <Button key={s.ns.join("|")} size="sm" variant="outline" className="h-7 font-mono text-[11px]" onClick={() => setSharedText(s.ns.join("\n"))} disabled={busy}>
                {s.ns.slice(0, 2).join(" + ")}{s.ns.length > 2 ? ` +${s.ns.length - 2}` : ""} <span className="ml-1 font-sans text-muted-foreground">({s.count})</span>
              </Button>
            ))}
          </div>
        )}
      </div>

      {/* 3. Plan + wykonanie */}
      <div className="space-y-3 rounded-lg border p-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <span className="font-medium">3. Plan:</span>
          <span>zmiana NS dla <strong>{plan.changeCount}</strong> z {selectedList.length} zaznaczonych</span>
          {plan.skipCount > 0 && (
            <span className="text-xs text-muted-foreground">
              pominięte: {skipCounts.same} już ustawione · {skipCounts.no_current} bez pobranych NS · {skipCounts.invalid_target} bez poprawnych nowych NS
            </span>
          )}
        </div>
        {skipCounts.no_current > 0 && !running && (
          <p className="text-xs text-amber-500">Domeny bez pobranych obecnych NS nie są zmieniane — kliknij „Pobierz obecne NS”, żeby widzieć, co nadpisujesz.</p>
        )}
        {running && (
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">Zapisuję po jednej domenie: {doneCount}/{Object.keys(runStates).length}</div>
            <Progress value={Object.keys(runStates).length ? (doneCount / Object.keys(runStates).length) * 100 : 0} className="h-1.5" />
          </div>
        )}
        {summary && !running && (
          <p className="text-xs">
            Wynik: <span className="text-emerald-500">zmienione {summary.ok}</span> · <span className={summary.failed ? "text-red-500" : ""}>błędy {summary.failed}</span> · pominięte {summary.skipped}
            {summary.stoppedReason === "consecutive_failures" && <span className="text-red-500"> · zatrzymano po 3 błędach z rzędu (sprawdź klucze konta AfterMarket), reszta domen nietknięta</span>}
            {summary.stoppedReason === "user" && <span> · zatrzymano ręcznie, reszta domen nietknięta</span>}
            . Każda zmiana jest w Cloudflare → Historia.
          </p>
        )}
        <div className="flex flex-wrap items-center justify-end gap-2">
          {running && (
            <Button size="sm" variant="outline" onClick={() => { stopRef.current = true; }}>
              <Square className="mr-2 h-3.5 w-3.5" /> Zatrzymaj po bieżącej domenie
            </Button>
          )}
          <Button onClick={() => setConfirmOpen(true)} disabled={busy || plan.changeCount === 0}>
            {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Zmień nameservery ({plan.changeCount})
          </Button>
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-400" />Zmienisz NS dla {plan.changeCount} domen · konto {WORKSPACE_LABEL[ws]}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 pt-2">
                {plan.skipCount > 0 && (
                  <p className="text-xs">Pominięte: {skipCounts.same} już ustawione · {skipCounts.no_current} bez pobranych NS · {skipCounts.invalid_target} bez poprawnych nowych NS.</p>
                )}
                <ul className="max-h-[45vh] space-y-2 overflow-auto rounded-md border p-2 text-xs">
                  {plan.rows.filter((r) => r.action === "change").map((r) => (
                    <li key={r.domain} className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-2 border-b pb-2 last:border-0 last:pb-0">
                      <div className="min-w-0">
                        <div className="font-mono font-semibold text-foreground">{r.domain}{r.source === "custom" && <Badge variant="outline" className="ml-2 px-1 py-0 text-[9px]">osobne</Badge>}</div>
                        {(r.currentNs?.length ? r.currentNs : ["brak ustawionych"]).map((n) => <div key={n} className="truncate font-mono">{n}</div>)}
                      </div>
                      <ArrowRight className="mt-4 h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="min-w-0 pt-4">{r.targetNs.map((n) => <div key={n} className="truncate font-mono text-emerald-400">{n}</div>)}</div>
                    </li>
                  ))}
                </ul>
                <p className="text-amber-400">Błędne NS wyłączają domenę do czasu poprawy. Zmiany idą po jednej domenie; po 3 błędach z rzędu run sam się zatrzyma.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anuluj</AlertDialogCancel>
            <AlertDialogAction onClick={() => void run()}>Tak, zmień NS dla {plan.changeCount} domen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
